import { Field as FormikField, FieldProps, type FormikErrors, type FormikTouched } from 'formik';
import * as React from 'react';
import { uid } from 'react-uid';
import { Field, List, ListItem } from '../../../design-system';
import {
  type FieldDefinition,
  formFieldHasError,
  type FormFields,
  type FormValues,
  getHelperText,
  HelperText,
} from '../../../utils/formUtils';
import { TextField } from '../../TextField';

type Props<Values extends FormValues, H = HelperText<Values>> = {
  fields: FormFields<Values, H>;
  errors: FormikErrors<Values>;
  touched: FormikTouched<Values>;
  disabled: boolean;
};

export function FormEditable<Values extends FormValues, H = HelperText<Values>>({
  disabled,
  errors,
  fields,
  touched,
}: Props<Values, H>) {
  const fieldsWithStringKeysType = fields as Record<string, FieldDefinition<Values, React.ReactNode>>;
  const fieldsArray = Object.values<FieldDefinition<Values, React.ReactNode>>(fieldsWithStringKeysType);

  return (
    <List>
      {fieldsArray.map((field, idx) => {
        const { id, label, inputComponent: Input, inputProps = {} } = field;

        const hasError = formFieldHasError({ key: id, touched, errors });
        const helperText = getHelperText({ key: id, touched, errors, helperText: inputProps?.helperText });

        const {
          InputProps,
          inputProps: inputElementProps,
          name: inputPropsName,
          onBlur: inputPropsOnBlur,
          onChange: inputPropsOnChange,
          onFocus,
          type,
          value: inputPropsValue,
        } = inputProps;

        return (
          <ListItem key={uid(field, idx)}>
            <Field label={label}>
              <FormikField name={id}>
                {({ field: formikField }: FieldProps<Values[keyof Values]>) => {
                  const {
                    checked,
                    multiple,
                    name: formikFieldName,
                    onBlur: formikFieldOnBlur,
                    onChange: formikFieldOnChange,
                    value: formikFieldValue,
                  } = formikField;

                  const name = inputPropsName ?? formikFieldName ?? '';
                  const onBlur = inputPropsOnBlur ?? formikFieldOnBlur;
                  const onChange = inputPropsOnChange ?? formikFieldOnChange;
                  const value = (inputPropsValue ?? formikFieldValue) as Values[keyof Values];

                  if (Input) {
                    return (
                      <Input
                        checked={checked}
                        disabled={disabled}
                        error={hasError}
                        helperText={helperText}
                        multiple={multiple}
                        name={name}
                        onBlur={onBlur}
                        onChange={onChange}
                        value={value}
                      />
                    );
                  }

                  return (
                    <TextField
                      InputProps={InputProps}
                      disabled={disabled}
                      error={hasError}
                      fullWidth
                      helperText={helperText}
                      inputElementProps={inputElementProps}
                      name={name}
                      onBlur={onBlur}
                      onChange={onChange}
                      onFocus={onFocus}
                      type={type}
                      value={value}
                    />
                  );
                }}
              </FormikField>
            </Field>
          </ListItem>
        );
      })}
    </List>
  );
}
